import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import UniversalAddressInput from './UniversalAddressInput';
import { 
  ArrowLeft,
  MapPin,
  CheckCircle,
  AlertCircle,
  Search,
  Map,
  ExternalLink,
  RefreshCw,
  Eye,
  Edit3,
  Save,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAddressValidation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [validationEntries, setValidationEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [correctedAddress, setCorrectedAddress] = useState('');
  const [addressData, setAddressData] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      toast.error('Admin access required');
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchValidationEntries();
    }
  }, [user]);

  const fetchValidationEntries = async () => {
    try {
      setLoading(true);
      
      // Get all manual entry addresses that need validation
      const [jobsResponse, usersResponse] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/admin/users`)
      ]);

      const entries = [];
      
      // Process jobs with manual entry locations
      jobsResponse.data.forEach(job => {
        if (job.location_manual_entry) {
          entries.push({
            id: job.id,
            entity_type: 'job_location',
            entity_id: job.id,
            current_address: job.location_formatted_address || job.location,
            current_lat: job.location_lat || job.lat,
            current_lng: job.location_lng || job.lng,
            manual_entry: true,
            created_at: job.created_at,
            status: 'pending',
            entity_details: `Job: ${job.title}`,
            originator: job.originator_business
          });
        }
        
        if (job.detailed_manual_entry && job.detailed_location) {
          entries.push({
            id: `${job.id}_detailed`,
            entity_type: 'job_detailed',
            entity_id: job.id,
            current_address: job.detailed_formatted_address || job.detailed_location,
            current_lat: job.detailed_lat,
            current_lng: job.detailed_lng,
            manual_entry: true,
            created_at: job.created_at,
            status: 'pending',
            entity_details: `Job (Detailed): ${job.title}`,
            originator: job.originator_business
          });
        }
      });

      // Process businesses with manual entry HQ addresses
      usersResponse.data.forEach(userEntry => {
        if (userEntry.hq_manual_entry && userEntry.hq_address) {
          entries.push({
            id: `${userEntry.id}_hq`,
            entity_type: 'business_hq',
            entity_id: userEntry.id,
            current_address: userEntry.hq_formatted_address || userEntry.hq_address,
            current_lat: userEntry.hq_lat,
            current_lng: userEntry.hq_lng,
            manual_entry: true,
            created_at: userEntry.created_at,
            status: 'pending',
            entity_details: `Business HQ: ${userEntry.business_name}`,
            originator: userEntry.business_name
          });
        }
      });

      setValidationEntries(entries);
      
    } catch (error) {
      console.error('Failed to fetch validation entries:', error);
      toast.error('Failed to load address validation data');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateAddress = async (entry, newAddressData) => {
    try {
      setUpdating(true);
      
      let updatePayload = {};
      
      if (entry.entity_type === 'job_location') {
        updatePayload = {
          location_formatted_address: newAddressData.formatted_address,
          location_lat: newAddressData.lat,
          location_lng: newAddressData.lng,
          location_place_id: newAddressData.place_id,
          location_manual_entry: false
        };
        await axios.put(`${API}/jobs/${entry.entity_id}`, updatePayload);
      } else if (entry.entity_type === 'job_detailed') {
        updatePayload = {
          detailed_formatted_address: newAddressData.formatted_address,
          detailed_lat: newAddressData.lat,
          detailed_lng: newAddressData.lng,
          detailed_place_id: newAddressData.place_id,
          detailed_manual_entry: false
        };
        await axios.put(`${API}/jobs/${entry.entity_id}`, updatePayload);
      } else if (entry.entity_type === 'business_hq') {
        updatePayload = {
          hq_formatted_address: newAddressData.formatted_address,
          hq_lat: newAddressData.lat,
          hq_lng: newAddressData.lng,
          hq_place_id: newAddressData.place_id,
          hq_manual_entry: false
        };
        await axios.put(`${API}/profile/location`, updatePayload);
      }
      
      toast.success('Address validated and updated successfully');
      setSelectedEntry(null);
      fetchValidationEntries(); // Refresh list
      
    } catch (error) {
      console.error('Failed to validate address:', error);
      toast.error('Failed to validate address');
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmAsIs = async (entry) => {
    try {
      setUpdating(true);
      
      // Mark as validated but keep manual entry
      let updatePayload = {};
      
      if (entry.entity_type === 'job_location') {
        updatePayload = { location_manual_entry: false };
        await axios.put(`${API}/jobs/${entry.entity_id}`, updatePayload);
      } else if (entry.entity_type === 'job_detailed') {
        updatePayload = { detailed_manual_entry: false };
        await axios.put(`${API}/jobs/${entry.entity_id}`, updatePayload);
      } else if (entry.entity_type === 'business_hq') {
        updatePayload = { hq_manual_entry: false };
        await axios.put(`${API}/profile/location`, updatePayload);
      }
      
      toast.success('Address confirmed as entered');
      fetchValidationEntries(); // Refresh list
      
    } catch (error) {
      console.error('Failed to confirm address:', error);
      toast.error('Failed to confirm address');
    } finally {
      setUpdating(false);
    }
  };

  const getGoogleMapsUrl = (lat, lng) => {
    if (lat && lng) {
      return `https://maps.google.com/?q=${lat},${lng}`;
    }
    return null;
  };

  const filteredEntries = validationEntries.filter(entry =>
    entry.current_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.entity_details?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.originator?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/admin')} 
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center">
                  <MapPin className="h-6 w-6 mr-2" />
                  Address Validation
                </h1>
                <p className="text-sm text-gray-600">Validate and correct manually entered addresses</p>
              </div>
            </div>
            
            <button
              onClick={fetchValidationEntries}
              className="jop-button-secondary flex items-center"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Stats */}
        <div className="jop-card p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Manual Address Entries</h2>
              <p className="text-sm text-gray-600">
                {filteredEntries.length} address{filteredEntries.length === 1 ? '' : 'es'} requiring validation
              </p>
            </div>
            
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="jop-input pl-10"
                placeholder="Search addresses, jobs, businesses..."
              />
            </div>
          </div>
        </div>

        {/* Validation Table */}
        <div className="jop-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coordinates
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        entry.entity_type === 'job_location' ? 'bg-blue-100 text-blue-800' :
                        entry.entity_type === 'job_detailed' ? 'bg-green-100 text-green-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {entry.entity_type === 'job_location' ? 'Job Location' :
                         entry.entity_type === 'job_detailed' ? 'Job Details' :
                         'Business HQ'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {entry.entity_details}
                      </div>
                      <div className="text-xs text-gray-500">
                        {entry.originator}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs">
                        {entry.current_address}
                      </div>
                      <div className="flex items-center mt-1">
                        <AlertCircle className="h-3 w-3 text-amber-500 mr-1" />
                        <span className="text-xs text-amber-600">Manual entry</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {entry.current_lat && entry.current_lng ? (
                        <div className="text-xs text-gray-600">
                          <div>{entry.current_lat.toFixed(6)}</div>
                          <div>{entry.current_lng.toFixed(6)}</div>
                          <button
                            onClick={() => window.open(getGoogleMapsUrl(entry.current_lat, entry.current_lng), '_blank')}
                            className="text-blue-600 hover:text-blue-800 flex items-center mt-1"
                          >
                            <Map className="h-3 w-3 mr-1" />
                            View
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No coordinates</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(entry.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(entry.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedEntry(entry);
                            setCorrectedAddress(entry.current_address);
                          }}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                          title="Validate Address"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleConfirmAsIs(entry)}
                          className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                          title="Confirm As Is"
                          disabled={updating}
                        >
                          <CheckCircle className="h-4 w-4" />
                        </button>
                        {entry.current_lat && entry.current_lng && (
                          <button
                            onClick={() => window.open(getGoogleMapsUrl(entry.current_lat, entry.current_lng), '_blank')}
                            className="text-gray-600 hover:text-gray-800 p-1 rounded hover:bg-gray-50"
                            title="View on Map"
                          >
                            <Map className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredEntries.length === 0 && (
              <div className="text-center py-12">
                <CheckCircle className="mx-auto h-12 w-12 text-green-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">All addresses validated!</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No manual address entries requiring validation.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Address Validation Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Validate Address</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedEntry.entity_details}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedEntry(null);
                    setCorrectedAddress('');
                    setAddressData(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Current Address */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Current Address (Manual Entry)</h4>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="text-sm text-gray-900">{selectedEntry.current_address}</div>
                  {selectedEntry.current_lat && selectedEntry.current_lng && (
                    <div className="text-xs text-gray-600 mt-1">
                      📍 {selectedEntry.current_lat.toFixed(6)}, {selectedEntry.current_lng.toFixed(6)}
                      <button
                        onClick={() => window.open(getGoogleMapsUrl(selectedEntry.current_lat, selectedEntry.current_lng), '_blank')}
                        className="text-blue-600 hover:text-blue-800 ml-2"
                      >
                        <ExternalLink className="h-3 w-3 inline" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Address Correction */}
              <div className="mb-6">
                <UniversalAddressInput
                  value={correctedAddress}
                  onChange={setCorrectedAddress}
                  onAddressSelect={setAddressData}
                  placeholder="Search for correct address..."
                  label="Corrected Address"
                  showCoordinates={true}
                  showPlaceId={true}
                />
              </div>

              {/* Map Comparison */}
              {addressData && addressData.lat && addressData.lng && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Map Preview</h4>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="text-sm text-green-900">{addressData.formatted_address}</div>
                    <div className="text-xs text-green-700 mt-1">
                      📍 {addressData.lat.toFixed(6)}, {addressData.lng.toFixed(6)}
                      <button
                        onClick={() => window.open(getGoogleMapsUrl(addressData.lat, addressData.lng), '_blank')}
                        className="text-green-600 hover:text-green-800 ml-2"
                      >
                        <ExternalLink className="h-3 w-3 inline" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={() => handleValidateAddress(selectedEntry, addressData)}
                  disabled={!addressData || updating}
                  className="jop-button flex-1 flex items-center justify-center"
                >
                  {updating ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Validate & Update
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setSelectedEntry(null);
                    setCorrectedAddress('');
                    setAddressData(null);
                  }}
                  className="jop-button-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAddressValidation;